import {
  AIR_MEASURE_STEP,
  DEFAULT_IMPORT_FEES_RATE,
  DEFAULT_IMPORT_FEES_CAP_THRESHOLD,
  DEFAULT_IMPORT_FEES_CAP_AMOUNT,
  DEFAULT_INSURANCE_TIERS,
  AVION_PAYMENT_RATIO,
  BATEAU_PAYMENT_RATIO,
} from "../constants/shipping";

/**
 * Frais d'import prélevés sur le sous-total au checkout.
 *
 * Ils complètent les frais bancaires déjà absorbés dans le prix de vente du
 * produit : 7 % dans le prix + 2 % ici = les 9 % de frais bancaires.
 * Le taux vient du backend (`GET /pricing`) ; la valeur par défaut n'est qu'un
 * repli le temps de l'appel.
 */
export const calcImportFees = (
  subtotal,
  rate = DEFAULT_IMPORT_FEES_RATE,
  capThreshold = DEFAULT_IMPORT_FEES_CAP_THRESHOLD,
  capAmount = DEFAULT_IMPORT_FEES_CAP_AMOUNT
) => {
  const fee = subtotal * rate;
  // Montant fixe sous `capThreshold`, indépendamment du taux — même règle que
  // `OrderFees::addFee()` côté backend, mêmes réglages (`import_fees_cap_*`).
  return Math.round(subtotal < capThreshold ? capAmount : fee);
};

/**
 * Assurance de la marchandise prélevée au checkout.
 *
 * Assise sur le seul sous-total : elle ne suit ni le transport estimé, ni le
 * transport final validé ou corrigé. Ce n'est plus un taux mais un montant
 * fixe par palier — le barème vient du backend (`GET /pricing`, clé
 * `insurance_tiers`, réglable en paramètres) ; `tiers` n'est qu'un repli le
 * temps de l'appel. Seule l'API fige le montant réel sur la commande.
 */
export const calcInsurance = (subtotal, tiers = DEFAULT_INSURANCE_TIERS) => {
  const amount = Number(subtotal) || 0;
  const list = Array.isArray(tiers) && tiers.length > 0 ? tiers : DEFAULT_INSURANCE_TIERS;
  const tier = list.find((t) => t.max == null || amount <= t.max);
  return tier ? Number(tier.amount) || 0 : 0;
};

/**
 * Tarif affiché d'un mode de transport, formaté pour le client.
 *
 * Les montants viennent toujours du barème backend (`GET /pricing`) : aucun
 * écran ne doit réécrire « 11 500 FCFA/kg » en dur, sinon le texte ment dès que
 * le réglage `amount_per_kg` ou `amount_per_cbm` change en base.
 *
 * @param {"aerien"|"maritime"|"fret aérien"|"fret maritime"} mode
 * @param {{perKg:number, perCbm:number}} pricing
 */
export const formatShippingRate = (mode, pricing) => {
  const isAir = mode === "aerien" || mode === "fret aérien";
  const amount = isAir ? pricing?.perKg : pricing?.perCbm;

  if (!Number.isFinite(amount) || amount <= 0) return null;

  return `${Math.round(amount).toLocaleString("fr-FR")} FCFA/${isAir ? "kg" : "CBM"}`;
};

/**
 * Phrase d'exemple du fret maritime : « 0,12 × 210 000 FCFA = 25 200 FCFA ».
 * Le tarif et le résultat suivent le barème backend.
 */
export const formatSeaFreightExample = (volume, pricing) => {
  const rate = pricing?.perCbm;

  if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(volume) || volume <= 0) {
    return null;
  }

  const fmt = (n) => Math.round(n).toLocaleString("fr-FR");

  return `Coût du transport : ${volume.toLocaleString("fr-FR")} × ${fmt(rate)} FCFA = ${fmt(volume * rate)} FCFA.`;
};

/** Calcule le coût de transport depuis la Chine */
export const calcShippingCost = (weight, ratePerKg) =>
  weight > 0 && ratePerKg > 0 ? Math.round(weight * ratePerKg) : 0;

/** Arrondi au demi-kilo supérieur (règle de facturation du fret aérien) */
export const roundUpHalfKg = (kg, step = AIR_MEASURE_STEP) =>
  kg > 0 ? Math.ceil(Number(kg.toFixed(3)) / step) * step : 0;

/**
 * Arrondi le montant final au 50 FCFA supérieur — pour simplifier le
 * paiement (espèces, Mobile Money). Même règle des deux côtés : voir
 * `Order::calculateTotal()` côté API, qui seule fige le montant réel.
 */
export const roundUpToFifty = (amount) => {
  const n = Number(amount) || 0;
  return n > 0 ? Math.ceil(n / 50) * 50 : 0;
};

/**
 * Poids total facturable des articles (kg), arrondi au demi-kilo supérieur.
 * L'arrondi porte sur le total, jamais sur chaque produit : 1,6 + 0,4 + 0,3
 * donne 2,5 kg et non 3 kg.
 */
export const billableWeight = (items = [], step = AIR_MEASURE_STEP) => {
  const total = items.reduce(
    (sum, i) => sum + (Number(i.weight) || 0) * (i.quantity ?? 1),
    0
  );
  return roundUpHalfKg(total, step);
};

/** Volume total des articles (CBM / m³) */
export const totalVolume = (items = []) =>
  items.reduce((sum, i) => sum + (Number(i.volume) || 0) * (i.quantity ?? 1), 0);

/**
 * Estime le coût de transport depuis la Chine selon les achats.
 * - Avion : poids total arrondi au 0,5 kg supérieur × tarif/kg
 * - Bateau : volume total (CBM) × tarif/CBM (pas d'arrondi)
 * @param {Array} items - articles avec { weight, volume, quantity }
 * @param {"aerien"|"maritime"} mode
 * @param {{perKg:number, perCbm:number}} rates
 * @returns {{ cost:number, quantity:number, unit:string, rate:number }}
 */
export const estimateTransport = (
  items = [],
  mode,
  { perKg = 0, perCbm = 0, airMeasureStep = AIR_MEASURE_STEP } = {}
) => {
  if (mode === "aerien") {
    const kg = billableWeight(items, airMeasureStep);
    return { cost: Math.round(kg * perKg), quantity: kg, unit: "kg", rate: perKg };
  }
  const cbm = totalVolume(items);
  return { cost: Math.round(cbm * perCbm), quantity: cbm, unit: "CBM", rate: perCbm };
};

/**
 * Calcule le montant de transport restant à payer à la réception.
 * - Avion : 20% de l'estimé
 * - Bateau : 100% de l'estimé (rien à la commande)
 */
export const calcRemainingShipping = (shippingCost, mode) => {
  if (mode === "aerien") {
    return Math.round(shippingCost * (1 - AVION_PAYMENT_RATIO));
  }
  return Math.round(shippingCost * (1 - BATEAU_PAYMENT_RATIO));
};

/**
 * Calcule l'acompte de transport à payer maintenant + le reste à réception.
 * - Avion : 80% maintenant, 20% à réception
 * - Bateau : 0% maintenant, 100% à réception (hors total commande)
 * @returns {{ ratio:number, amount:number, remaining:number, label:string, remainingLabel:string }}
 */
export const getShippingPaymentInfo = (shippingCost, mode, airPrepaidRate = AVION_PAYMENT_RATIO) => {
  const cost = Number(shippingCost) || 0;

  if (mode === "aerien") {
    const amount = Math.round(cost * airPrepaidRate);
    const remaining = Math.max(0, cost - amount);
    return {
      ratio: airPrepaidRate,
      amount,
      remaining,
      label: `${Math.round(airPrepaidRate * 100)}% des frais de transport estimés`,
      remainingLabel: `${Math.round((1 - airPrepaidRate) * 100)}% des frais de transport estimés à la réception`,
    };
  }

  // Bateau : rien à la commande, tout à l'arrivée
  return {
    ratio: BATEAU_PAYMENT_RATIO,
    amount: 0,
    remaining: cost,
    label: "Payé à l'arrivée",
    remainingLabel: "100% des frais de transport estimés à l'arrivée",
  };
};
